import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { useI18n } from '../../lib/i18n/I18nProvider';
import { newIntent, useApiAction, useApiList, type ActionIntent } from '../../lib/api/queries';
import type { AttributionException, CustomerView, LeadRow, TimelineEntry } from '../../lib/api/types';
import { Badge, Card, DataTable, Field, Ltr, PageTitle, Select, TextArea } from '../../ui/primitives';
import { DegradedState, EmptyState, ErrorState, LoadingState, SuccessNote } from '../../ui/states';
import { ApprovalHandoff, AuditStatement, RefusalWithRemedy, StepUpPanel } from '../../ui/guards';
import { ApiError } from '../../lib/api/problem';
import { useSession } from '../../lib/auth/session';
import type { HandoffGrant, HandoffRequest } from '../../lib/auth/handoff';
import { statusTone } from './Expos';

/** The closed category set: an ordinary commercial claim is NOT a category here. */
const EXCEPTION_CATEGORIES = ['administrative_error', 'identity_merge_split', 'fraud', 'data_integrity'];

const REVEAL = 'customer.pii.reveal';
const EXCEPTION_RESOLVE = 'attribution.exception.resolve';
const IDENTITY_MERGE = 'identity.merge';

/** `ADM-14` the masked register · `ADM-15` the attribution timeline. */
export function AdminCustomers(): ReactElement {
  const { t, formatDateTime } = useI18n();
  const [expoId, setExpoId] = useState('');
  const customers = useApiList<CustomerView>('/v1/customers', expoId ? { expo_id: expoId } : undefined);
  const [selected, setSelected] = useState<string | null>(null);

  return (
    <>
      <PageTitle title={t('admin.customers.title')}>{t('admin.customers.desc')}</PageTitle>

      <Card title={t('admin.customers.title')}>
        <Field label="expo_id" name="customer-expo" value={expoId} onChange={setExpoId} ltr />
        {customers.isLoading ? <LoadingState /> : null}
        {customers.error ? <ErrorState error={customers.error} onRetry={() => void customers.refetch()} /> : null}
        {customers.data ? (
          <DataTable<CustomerView>
            caption={t('admin.customers.title')}
            rows={customers.data.items ?? []}
            rowKey={(c) => c.id}
            empty={<EmptyState>{t('admin.customers.desc')}</EmptyState>}
            columns={[
              {
                key: 'id',
                header: t('table.identifier'),
                cell: (c) => (
                  <button type="button" className="btn btn--secondary btn--small" onClick={() => setSelected(c.id)}>
                    <Ltr>{c.id.slice(0, 8)}</Ltr>
                  </button>
                ),
              },
              {
                key: 'phone',
                header: 'phone',
                cell: (c) => (
                  // A masked value keeps its unmasked fragment `ltr` and the mask in flow order.
                  <span>
                    <Ltr>{c.phone_masked}</Ltr>
                    {c.phone_e164 ? (
                      <>
                        {' '}
                        <Badge tone="warn">revealed</Badge> <Ltr>{c.phone_e164}</Ltr>
                      </>
                    ) : null}
                  </span>
                ),
              },
              { key: 'locale', header: 'locale', cell: (c) => <Ltr>{c.locale}</Ltr> },
              {
                key: 'status',
                header: t('table.status'),
                cell: (c) => <Badge tone={statusTone(c.status)}>{c.status}</Badge>,
              },
              { key: 'created', header: t('table.created'), cell: (c) => formatDateTime(c.created_at) },
            ]}
          />
        ) : null}
      </Card>

      <RevealPanel customerId={selected} onRevealed={() => void customers.refetch()} />

      {selected ? <CustomerTimeline customerId={selected} /> : null}
    </>
  );
}


interface RevealedCustomer {
  id: string;
  phone_masked?: string;
  phone_e164?: string;
  full_name?: string;
  email?: string;
  locale?: string;
}

/**
 * The fields whose presence means SOMETHING WAS DECRYPTED.
 *
 * `crm.Customer` writes its `customer.pii.revealed` row inside the branch that decrypts, and that
 * branch is not always taken: a customer whose `phone_source` is not `crm` holds Identity & Access's
 * envelope under ITS AAD, so the service returns `phone_masked: "•••"` and RETURNS EARLY — 200, no
 * plaintext, and no audit row. A screen that reads 200 as "the disclosure was audited" is claiming a
 * compliance record that does not exist for the one case where nothing was disclosed either.
 */
const DECRYPTED_FIELDS = ['phone_e164', 'full_name', 'email'] as const;

/** One row of the LOCAL, in-tab record. It is not the server's audit trail and is never sent. */
interface RevealLogEntry {
  /** Distinguishes two attempts at the SAME intent — a replay carries the same key and subject. */
  seq: number;
  at: string;
  customerId: string;
  purpose: string;
  caseRef: string;
  outcome: string;
  /**
   * WHAT THE SERVER'S OWN TRAIL HOLDS FOR THIS ATTEMPT, and never more than that:
   *
   *   - `written` — decrypted fields came back, so the row committed with them;
   *   - `nothing_revealed` — 200, but no plaintext: the early return above wrote nothing;
   *   - `unavailable` — `422 customer.reveal_unavailable`: the CRM holds no number for this person,
   *     so there was nothing to disclose, nothing to audit, and no grant was spent (BE-6);
   *   - `refused` — the action did not run, so neither did the audit;
   *   - `unknown` — NO ANSWER THIS TAB CAN TRUST (round-4 A4, widened in round-5). `fetch` rejected,
   *     OR the server answered 5xx: either way this tab cannot say whether the disclosure committed.
   *     Calling that "refused" would be a false entry in the one record this screen actually owns;
   *     the honest entry says the outcome is not known here, and the replay under the same key is
   *     what settles it. See `ApiError.outcomeUnknown` for why a 5xx belongs in this row and not the
   *     one above it.
   *   - `in_progress` — THE ATTEMPT HAS NOT FINISHED (P13-n). `409 idempotency.in_progress`: the
   *     server says the request under this key is still executing. It is neither of the two rows
   *     around it — not `refused`, because nothing was refused, and not `unknown`, because this tab
   *     was told something quite specific — so it gets its own words. The entry records the state of
   *     an attempt still being processed, and the collect under the same key is what turns it into
   *     one of the rows above.
   */
  audit: 'written' | 'nothing_revealed' | 'unavailable' | 'refused' | 'unknown' | 'in_progress';
}

/** What the operator stated for one attempt — recorded with it, and re-sent verbatim on a replay. */
interface Justification {
  purpose: string;
  caseRef: string;
}

/**
 * AN ATTEMPT WHOSE OUTCOME IS STILL OPEN, HELD AGAINST THE PERSON IT WAS ABOUT.
 *
 * It carries everything the two legitimate ways out need, because both of them may be taken long
 * after the operator has looked at other rows:
 *
 *   - `intent` — THE frozen `Idempotency-Key`, which is the only handle on that attempt's outcome.
 *     It is held here rather than read from `useApiAction`, which keeps one `lastIntent` for the
 *     whole hook: a reveal for another customer in between moves that slot, and a replay that took
 *     it would send somebody else's disclosure request under somebody else's key.
 *   - `purpose` / `caseRef` — what THAT attempt stated, so the abandonment entry and any replay
 *     record the justification actually sent rather than whatever is in the fields now.
 *   - `error` — the answer that left it open, so returning to the row restores the server's own
 *     words for it: the code, the detail, and the request id support needs.
 *   - `kind` — WHICH KIND OF OPEN IT IS (P13-n), because the panel and the record must say the true
 *     one. `unknown` is ignorance: nothing came back, or a 5xx that named nothing. `in_progress` is
 *     the opposite of ignorance — the server answered `409 idempotency.in_progress`, which says the
 *     first attempt is executing right now. The two share every RULE hanging off this state (the key
 *     is kept, the control stands down, collect and the priced abandon are the ways out) and share
 *     no wording at all: telling an operator "the outcome is not known here" about an act the server
 *     has just said is running would be as false as calling it refused.
 */
type OpenKind = 'unknown' | 'in_progress';

interface OpenAttempt extends Justification {
  intent: ActionIntent;
  error: unknown;
  kind: OpenKind;
}

/** The decrypted fields this response actually carried. */
function disclosedFields(c: RevealedCustomer): string[] {
  return DECRYPTED_FIELDS.filter((k) => {
    const v = c[k];
    return typeof v === 'string' && v.trim() !== '';
  });
}

/**
 * THE CLIENT'S RECORD OF THE GRANT SURVIVES THIS OUTCOME (round-4 A4).
 *
 * `session.releaseGrant` deletes this tab's record that the operator holds a live step-up for
 * `customer.pii.reveal`. The SERVER's grant is spent by `ConsumeStepUpTxAt`, inside the transaction
 * that decrypts and audits — so the client may let go of its record only when it knows that
 * happened, and the honest reading of "knows" is narrow:
 *
 *   - A SUCCESS spent it. One grant, one person; the next reveal needs a new factor.
 *   - A `401 auth.step_up_required` / `auth.assurance_insufficient` says there IS no live grant.
 *     Whatever became of it, the badge describes an elevation the server has just denied, and the
 *     remedy the refusal draws is the step-up that replaces it.
 *   - EVERYTHING ELSE KEEPS IT, because everything else either proves the grant intact or proves
 *     nothing at all:
 *       · `422 customer.reveal_unavailable` — the service refuses above `ConsumeStepUpTxAt` (BE-6);
 *       · `422 customer.reveal_justification_required` — `crm.RevealRequest.Validate` refuses in the
 *         same place, before anything is decrypted;
 *       · `400 request.validation_failed` — `decodeStrict` refused the body; no domain ran;
 *       · `403` — `guardCustomerReveal` refused ahead of the idempotency layer entirely;
 *       · a TRANSPORT FAILURE, and this is the case the rule exists for. `fetch` rejected: the
 *         request may have committed a disclosure or may never have left the tab. Dropping the
 *         record here sends the operator to collect a second factor for a reveal that may already
 *         have happened — and the retry that carries the same `Idempotency-Key` back to the server,
 *         which is the ONLY way to learn which it was, is exactly what the dropped record disables.
 *       · `409 idempotency.in_progress` — the server says the request under this key has not
 *         finished (P13-n). It is raised by the idempotency layer above the handler, so it cannot
 *         itself have consumed anything, and the act it names may be inside `ConsumeStepUpTxAt` at
 *         that very moment. Dropping the record here asks for a second factor for a disclosure that
 *         is happening, and takes away the elevation the collect under this key would need.
 *       · a 5xx, for the same reason and NOT for the reason this comment used to give. It said the
 *         idempotency layer abandons rather than stores a 5xx, so the key simply runs afresh. That
 *         is true of a fault the HANDLER raised — `Deps.idempotent` abandons the claim and discards
 *         what the handler recorded, because the handler's transaction rolled back with it — and it
 *         is not true of the 5xx a browser actually sees, which may equally be a proxy's 502, a 504
 *         on a slow commit, or a connection cut after the COMMIT. Since BE-10 the reveal's
 *         idempotent outcome is written by `crm.RevealRequest.Record` INSIDE the CRM transaction, so
 *         a disclosure that committed has its outcome stored with it: the replay says which world
 *         this is, and the grant is kept until it does. A TYPED 5xx REFUSAL is kept for the opposite
 *         reason (P13-m): `503 auth.step_up_unavailable` and `503 idempotency.encryption_unavailable`
 *         are decisions, and what they decided is that the act never ran — the first says in as many
 *         words that "a one-action grant cannot be spent", and README delta 24.af says a client must
 *         not answer it by collecting a factor. So it is not an unknown, and it is not a spend.
 *
 * The asymmetry is deliberate: a kept record that turns out to be stale costs one refused request
 * whose remedy is drawn on screen, while a dropped record that turns out to be live costs a second
 * factor AND the collectability of the first outcome.
 */
function grantSurvives(e: unknown): boolean {
  return !(e instanceof ApiError && e.needsStepUp);
}

/**
 * `ADM-14`'s reveal, which actually reveals.
 *
 * The old card raised a step-up for `customer.pii.reveal` and stopped there: no request was made, no
 * purpose was collected, and nothing was shown — so the operator spent a second factor on nothing
 * and the register stayed masked.
 *
 * THE REVEAL IS AN ACTION, NOT A READ (BE-8): `POST /v1/customers/{id}:reveal` with the body
 * `{purpose, case_reference}`. It was `GET …?reveal=true&purpose=…&case_reference=…`, and the method
 * was the lie: the act spends a one-use step-up grant and writes a disclosure row, so a GET put the
 * only credential-spending surface in the API inside the anti-CSRF exemption, wrote the operator's
 * prose about a named person into every access log on the path, and — carrying no `Idempotency-Key`
 * — left a lost response unrecoverable. The screen therefore sends it the way it sends every other
 * mutation: one `newIntent`, one uuidv7 key, frozen and re-sent verbatim by `retryLast`.
 *
 * `guardCustomerReveal` admits only a caller who holds `customer.pii.reveal` and, in the idempotency
 * layer's own guard, is inside a step-up window raised for that action; `crm.Customer` writes its
 * `aud_events` row inside the same transaction as the decrypt, so the audit and the disclosure commit
 * together or not at all.
 *
 * WHAT THE SERVER RECORDS, EXACTLY (BE-4, reversing round-2 H7 / round-3 #15): `{action:
 * "customer.pii.revealed", subject_type: "crm_customers", subject_id: <id>}` plus the actor and
 * request id every audit row carries, AND `detail: {purpose, case_reference, record_version}` with
 * the case reference also in `aud_events.reason`. The two fields are REQUIRED by the route — the
 * strict decoder takes exactly them and the domain refuses
 * `422 customer.reveal_justification_required` without them — so the screen states them as recorded,
 * which is what doc 04 ADM-14 asks it to do.
 *
 * THIS CLAIM HAS NOW BEEN WRONG IN BOTH DIRECTIONS, and the rule that settles it is the same each
 * time: say what the route this build talks to actually writes. It claimed a record that did not
 * exist; P13-i corrected it to "not recorded"; the backend then made the record real, and leaving
 * the disclaimer would understate a compliance obligation the operator HAS discharged — and would
 * describe as tab-local two values the server refuses to proceed without.
 *
 * THE GRANT IS SPENT BY THE ACT (BE-2): one step-up, one person — and the client's own record of it
 * follows `grantSurvives` above, which keeps it for every outcome that does not prove it gone.
 *
 * WHAT IT ALSO DOES NOT DO: pretend there is an audit-trail read route. This branch serves none, so
 * the server-side trail shown is the one the server does serve — the attribution timeline.
 *
 * AND IT NO LONGER READS `200` AS `AUDITED` (round-3 #15). `crm.Customer` returns early for a
 * customer whose phone did not come from the CRM: masked value, no plaintext, no `aud_events` row,
 * status 200. The audit claim now follows the DISCLOSURE — the decrypted fields the response
 * actually carried — so the one outcome where nothing was revealed is labelled as itself instead of
 * being counted as a recorded disclosure that never happened.
 */
function RevealPanel({
  customerId,
  onRevealed,
}: {
  customerId: string | null;
  onRevealed: () => void;
}): ReactElement {
  const { t, formatDateTime } = useI18n();
  const session = useSession();
  /**
   * THE ACTION, WITH THE REPO'S OWN IDEMPOTENCY MACHINERY (BE-8).
   *
   * `action` is deliberately NOT set on the intents this panel mints. `useApiAction` keeps the
   * grant record for an answer that decided nothing and spends it for every answer the server DID
   * decide — the right rule for a mutation whose decided refusals say nothing about the factor, and
   * still the wrong one here, where SEVERAL TYPED 4xx REFUSALS PROVE THE GRANT INTACT:
   * `customer.reveal_unavailable`, `customer.reveal_justification_required`, a strict-decoder 400
   * and the route guard's 403 are all raised above `ConsumeStepUpTxAt`. This panel owns that rule;
   * see `grantSurvives`.
   */
  const act = useApiAction<RevealedCustomer>();
  const [purpose, setPurpose] = useState('');
  const [caseRef, setCaseRef] = useState('');
  const [revealed, setRevealed] = useState<RevealedCustomer | null>(null);
  const [log, setLog] = useState<RevealLogEntry[]>([]);
  const seq = useRef(0);
  const [error, setError] = useState<unknown>(null);
  /**
   * `customer.reveal_unavailable` IS NOT AN ERROR (BE-6), so it does not live in `error`.
   *
   * The request was well formed, the caller was entitled, and the CRM simply holds no number for
   * this person — the record carries another context's envelope. Routing it through
   * `RefusalWithRemedy` would offer a retry and a step-up for an outcome no factor and no repetition
   * can change; the code is deliberately outside the backend's `isRetryableRefusal` for the same
   * reason. It is its own named state instead.
   */
  const [unavailable, setUnavailable] = useState(false);
  /**
   * AN ATTEMPT WHOSE OUTCOME IS NOT SETTLED, AND THE SCREEN SAYS WHICH KIND (round-4 A4; widened in
   * round-5 A4 and again in P13-n).
   *
   * A transport failure is not a refusal and must not be drawn as one: the disclosure may have
   * committed. NEITHER IS AN UNTYPED 5xx — that was the round-5 finding. Only `status === 0` set
   * this state, so a 500 or a 502 was routed through the refusal surface and written into the local
   * record as "refused", which is a claim about the server's own trail that this tab is in no
   * position to make. `ApiError.outcomeUnknown` is the classification those two share — and it now
   * excludes the two typed 503s the backend raises for an act it refused before running (P13-m).
   *
   * AND NEITHER IS `409 idempotency.in_progress` (P13-n), WHICH IS THE WORST OF THE THREE TO GET
   * WRONG. It does not merely fail to decide the act — it says, in a typed answer, that the act is
   * BEING PERFORMED RIGHT NOW under the key this panel is holding. Settling on it deleted the frozen
   * intent, wrote "refused" against a disclosure the server was in the middle of making, and handed
   * back the control that mints a new key. The answer most likely to meet a replay was the answer
   * that destroyed the ability to replay. It is the same open state, under its own name and its own
   * words: see `OpenAttempt.kind` and `ApiError.isPendingOutcome`.
   *
   * It is its own state because the operator needs two facts an `ErrorState` cannot carry — their
   * factor was NOT spent by this tab, and the act beside it re-sends the same `Idempotency-Key`,
   * so the server answers with the stored outcome rather than performing a second reveal.
   *
   * AND IT IS HELD PER SUBJECT (P13-m ITEM 1). It used to be one boolean cleared on every change of
   * `customerId`, which made selecting a neighbouring row a second, silent way out of the block that
   * round-5 A4(c) built — no warning, no record, and the ambiguous intent gone with it. An attempt
   * belongs to the person it was made about, so it waits on that row: switching away decides nothing
   * about it, and coming back restores the panel and both of its acts. Nothing is discarded except
   * by `abandonAttempt`, which says what it costs first.
   *
   * WHILE ONE STANDS, THIS PANEL SENDS NOTHING ELSE FOR THAT SUBJECT — see `run` and the control
   * below it. Another subject is unaffected: its reveal mints its own key and destroys nothing.
   */
  const [openBySubject, setOpenBySubject] = useState<Record<string, OpenAttempt>>({});
  const pending = customerId === null ? null : (openBySubject[customerId] ?? null);

  /**
   * The answer on screen: this row's decided refusal, or the one that left its question open.
   *
   * The second half is what makes the restored panel whole. A decided refusal is cleared when the
   * subject changes (below), but the answer behind an open attempt is held with the attempt, so
   * returning to the row draws the server's own words for it again — and `RefusalWithRemedy` reads
   * the classification off it to offer the replay rather than a fresh act: `outcomeUnknown` draws
   * the "try again" on the error state, and `isIdempotencyInProgress` draws the collect on the
   * decided-conflict surface. Both call `collect`, which is THIS subject's frozen key (P13-n): the
   * in-progress answer is not given a second collect surface of its own, because the one the guard
   * already draws for it is the right one — it just needed to be pointed at the held intent.
   */
  const shownError = error ?? pending?.error ?? null;

  const grant = session.grantFor(REVEAL);
  const ready = customerId !== null && purpose.trim() !== '' && caseRef.trim() !== '' && grant !== null;
  const busy = act.isPending;

  /**
   * WHICH SUBJECT IS IN FRONT OF THE OPERATOR RIGHT NOW.
   *
   * An answer can land after the selection has moved — the request is in flight and the operator
   * clicks another row — and the states that DRAW something about a person must not then draw it
   * under a different one. The states keyed by subject (the local record, the unknown above) are
   * always written; the displayed ones are written only while their subject is still displayed.
   */
  const shownSubject = useRef<string | null>(customerId);

  /**
   * CHANGING THE SELECTED CUSTOMER CLEARS WHAT WAS REVEALED (round-2 M10).
   *
   * The panel is driven by whichever row the register has selected, and the revealed block was
   * keyed to nothing: picking a different customer left the previous person's unmasked number on
   * screen under the new selection. It is now dropped the instant the subject changes — a disclosure
   * belongs to the person it was made about.
   *
   * THE UNKNOWN OUTCOME IS DELIBERATELY NOT IN THIS LIST. Clearing a disclosure protects the person;
   * clearing an unanswered question destroys the only handle on one that may already have been made.
   * It is keyed by subject instead, and only `abandonUnknown` drops one.
   */
  useEffect(() => {
    shownSubject.current = customerId;
    setRevealed(null);
    setError(null);
    setUnavailable(false);
  }, [customerId]);

  /** Appends one attempt to the tab's own record. A replay of one intent is its own entry. */
  const record = useCallback(
    (entry: Omit<RevealLogEntry, 'seq' | 'at'>) => {
      seq.current += 1;
      setLog((l) => [{ ...entry, seq: seq.current, at: new Date().toISOString() }, ...l]);
    },
    [setLog],
  );

  /**
   * SENDS ONE INTENT — a first attempt or the replay of one — with the handling every attempt owes.
   *
   * The subject and the justification travel WITH the intent rather than being read from state,
   * because a replay may be collected long after the operator has looked at other rows: the entry it
   * writes in the tab's record must name the person that attempt was about and the words it actually
   * carried, not whichever row is selected and whatever is in the fields now.
   */
  const send = useCallback(
    (intent: ActionIntent, subject: string, why: Justification) => {
      /** Drops this subject's open question: its outcome has just been answered. */
      const settle = (): void =>
        setOpenBySubject((m) => {
          if (!(subject in m)) return m;
          const next = { ...m };
          delete next[subject];
          return next;
        });

      act.mutate(intent, {
        onSuccess: (data) => {
          // EVERY OUTCOME DECIDES EVERY STATE IT COULD HAVE SET. A replay re-sends the intent
          // directly rather than through the click handler, so an answer that finally arrives must
          // clear what the lost attempt put on screen — otherwise the collected reveal is drawn
          // underneath a panel still saying nothing came back.
          settle();
          // THE AUDIT CLAIM FOLLOWS THE DISCLOSURE AND NOTHING ELSE. `crm.Customer` writes the
          // `customer.pii.revealed` row inside the branch that decrypts — so the evidence that the row
          // committed is plaintext in the response, not the status line. Nothing here is optimistic:
          // the note below is not drawn until this line has run.
          const disclosed = disclosedFields(data);
          if (shownSubject.current === subject) {
            setError(null);
            setUnavailable(false);
            setRevealed(data);
          }
          record({
            customerId: subject,
            purpose: why.purpose,
            caseRef: why.caseRef,
            outcome: disclosed.length > 0 ? `revealed: ${disclosed.join(', ')}` : 'nothing revealed',
            audit: disclosed.length > 0 ? 'written' : 'nothing_revealed',
          });
          // The server consumed the step-up inside the transaction that decrypted; the client must
          // not go on claiming it.
          session.releaseGrant(REVEAL);
          onRevealed();
        },
        onError: (e) => {
          // A REVEAL THE CRM CANNOT PERFORM IS ITS OWN OUTCOME, NOT A FAULT (BE-6). `crm.Customer`
          // refuses before it reaches `ConsumeStepUpTxAt`, and says so in the detail it sends: "No
          // step-up grant was spent and no disclosure was recorded".
          const cannot = e instanceof ApiError && e.code === 'customer.reveal_unavailable';
          // AND AN ANSWER THIS TAB CANNOT TRUST IS NOT AN OUTCOME AT ALL (round-4 A4, round-5 A4): a
          // lost connection AND an untyped 5xx, which are the same ignorance with and without a
          // status line. A TYPED 5xx refusal is not among them (P13-m ITEM 3): it is a decision that
          // the act did not run, so it is drawn as the refusal it is and holds nothing still.
          const lost = e instanceof ApiError && e.outcomeUnknown;
          // NOR IS AN ACT THE SERVER SAYS IS STILL RUNNING (P13-n). `409 idempotency.in_progress`
          // means the attempt under this key has not finished — so the disclosure may be committing
          // as this line runs, and the key is the only thing that will ever be able to say. It is
          // held open exactly like the unknown, and it is NOT the unknown: see `OpenAttempt.kind`.
          const running = e instanceof ApiError && e.isPendingOutcome;
          const kind: OpenKind | null = lost ? 'unknown' : running ? 'in_progress' : null;
          // Set from the answer, not toggled: a replay lands here too (see `onSuccess`).
          if (kind) setOpenBySubject((m) => ({ ...m, [subject]: { intent, ...why, error: e, kind } }));
          else settle();
          if (shownSubject.current === subject) {
            setRevealed(null);
            setUnavailable(cannot);
            // An open attempt's answer is held WITH the attempt and rendered through `shownError`,
            // so that returning to this row draws it again — for the in-progress one too, whose
            // collect surface is the decided-conflict panel `RefusalWithRemedy` already draws.
            setError(cannot || kind !== null ? null : e);
          }
          record({
            customerId: subject,
            purpose: why.purpose,
            caseRef: why.caseRef,
            outcome: e instanceof ApiError ? e.code : 'failed',
            // THREE OUTCOMES, THREE ENTRIES, AND NONE OF THEM BORROWS ANOTHER'S CLAIM. "Refused" is
            // reserved for an answer that refused; an attempt still being processed says so in its
            // own words. Each `OpenKind` is named for the entry it writes, so an open attempt is
            // recorded as what it is and only a settled refusal falls through to `refused`.
            audit: cannot ? 'unavailable' : (kind ?? 'refused'),
          });
          if (!grantSurvives(e)) session.releaseGrant(REVEAL);
        },
      });
    },
    [act, onRevealed, record, session],
  );

  const run = useCallback(() => {
    if (!customerId) return;
    /**
     * A FRESH EXECUTION IS REFUSED WHILE THIS SUBJECT'S EARLIER ATTEMPT IS OPEN (round-5 A4).
     *
     * `newIntent` below mints a NEW `Idempotency-Key`, so starting a fresh reveal for the same
     * person overwrites nothing on the server but leaves this tab holding two questions and one
     * answer — and the operator most likely to press it is the one looking at "the outcome is not
     * known here". The control that calls this is disabled for the same reason; the guard is here as
     * well because the invariant belongs to the act, not to the button. The two ways out are
     * `collect` (ask under that key) and `abandonAttempt` (say so, on purpose).
     *
     * IT HOLDS FOR THE IN-PROGRESS ATTEMPT TOO (P13-n), and there it is not even ambiguous: the
     * server has said that request is running, so a second key would start a SECOND disclosure of
     * the same person while the first one completes.
     */
    if (pending) return;
    setError(null);
    setRevealed(null);
    setUnavailable(false);
    const why: Justification = { purpose: purpose.trim(), caseRef: caseRef.trim() };
    /**
     * ONE INTENT, ONE KEY — minted here, per act the operator asked for, and re-sent verbatim by
     * `collect`. That is what makes a lost outcome collectable: the server stores the outcome under
     * the key and replays it, so the recovery costs neither a second grant nor a second
     * `customer.pii.revealed` row.
     */
    const intent = newIntent({
      path: `/v1/customers/${customerId}:reveal`,
      // Exactly the two fields `decodeStrict` names. `reveal` is not among them: the method IS the
      // assertion now, and over-posting the old flag would be a 400 before anything is decrypted.
      body: { purpose: why.purpose, case_reference: why.caseRef },
      label: t('reveal.title'),
    });
    send(intent, customerId, why);
  }, [customerId, purpose, caseRef, pending, send, t]);

  /**
   * ASKS THE SERVER WHAT IT DID, UNDER THE KEY THAT ATTEMPT USED.
   *
   * It replays THIS SUBJECT's held intent, not `act.retryLast`: the hook keeps one `lastIntent` for
   * the whole panel, and a reveal for another customer in between moves it. `retryLast` remains the
   * right thing for a decided refusal, which can only ever be the attempt just made.
   *
   * ONE FUNCTION SERVES BOTH SURFACES (P13-n). `RefusalWithRemedy` draws "try again" for an unknown
   * outcome and "collect the outcome" for `idempotency.in_progress`, and both are the same act on
   * the same frozen key — so the in-progress answer is wired to this rather than given a duplicate
   * button inside the open-attempt panel.
   */
  const collect = useCallback(() => {
    if (customerId && pending) {
      send(pending.intent, customerId, { purpose: pending.purpose, caseRef: pending.caseRef });
      return;
    }
    act.retryLast();
  }, [act, customerId, pending, send]);

  /**
   * THE OPERATOR DROPS THE OPEN ATTEMPT, DELIBERATELY AND ON THE RECORD (round-5 A4).
   *
   * The replay is the right way out of an unsettled outcome, and it is not always available — a
   * backend that is down for the afternoon answers nothing to collect. So there is a second way out,
   * and it is a decision rather than a side effect: the panel states what abandoning costs (this
   * tab's only handle on that attempt, since the next reveal mints a new key) and the operator says
   * yes to that sentence. What it does NOT do is claim an outcome: the abandoned attempt's own entry
   * stands as it was written, and a second entry records that it was abandoned uncollected, because
   * that is a thing that happened to the one record this screen owns.
   *
   * IT IS THE ONLY PATH THAT DISCARDS ONE (P13-m ITEM 1), it discards exactly the subject in front
   * of the operator, and it records the justification THAT attempt carried rather than whatever the
   * fields hold now. It reads `unknown` whichever kind was dropped (P13-n): once the handle is gone
   * this tab cannot learn what became of it, and that is true of an attempt that was still running
   * just as much as of one that never answered.
   */
  const abandonAttempt = useCallback(() => {
    if (!customerId || !pending) return;
    setOpenBySubject((m) => {
      const next = { ...m };
      delete next[customerId];
      return next;
    });
    setError(null);
    record({
      customerId,
      purpose: pending.purpose,
      caseRef: pending.caseRef,
      outcome: 'abandoned, outcome never collected',
      audit: 'unknown',
    });
  }, [customerId, pending, record]);

  return (
    <Card title={t('reveal.title')} description={t('reveal.desc')}>
      {/* ROUND-5 A4(d) — IT IS NOT A READ, AND THIS PARAGRAPH SAID SO TWICE.
          "A separate, step-up-gated read … that read also SPENDS the step-up grant" is a sentence
          arguing with its own noun. The act is `POST …:reveal`: it decrypts, writes a
          `customer.pii.revealed` row naming this operator and this customer, and burns a one-use
          factor. An operator who believes they are looking something up does not believe they are
          leaving that record — which is the whole misunderstanding this statement exists to
          prevent.

          P13-m ITEM 4 — AND IT SAYS IT IN BOTH LANGUAGES. The correction was left as hardcoded
          English JSX on an Arabic-FIRST surface, so the statement that exists to prevent that
          misunderstanding could not be read by the operators these dashboards default to. */}
      <AuditStatement>
        <p>{t('reveal.statement')}</p>
        <p>{t('reveal.statement.spends')}</p>
      </AuditStatement>

      {customerId === null ? <EmptyState title={t('reveal.none')} /> : null}

      <Field label={t('reveal.purpose')} name="reveal-purpose" value={purpose} onChange={setPurpose} required />
      <Field label={t('reveal.case')} name="reveal-case" value={caseRef} onChange={setCaseRef} ltr required />

      {/* THE STANDING PROMPT, AND ONLY WHEN NOTHING ELSE IS ALREADY DRAWING ONE. A refusal that
          needs step-up draws its own through `RefusalWithRemedy`, and releasing the grant record on
          that refusal (see `grantSurvives`) would otherwise put a second, identical panel on the
          screen — two controls for one factor, only one of which returns to this action. */}
      {grant || (shownError instanceof ApiError && shownError.needsStepUp) ? null : (
        <StepUpPanel action={REVEAL} />
      )}

      {/* THE ONE OUTCOME THAT IS NEITHER A DISCLOSURE NOR A FAULT (BE-6). No alert, no retry, no
          step-up remedy: the CRM holds no number for this person, and nothing the operator can do
          from here changes that. */}
      {unavailable ? (
        <div data-testid="reveal-unavailable">
          <DegradedState title={t('reveal.unavailable')}>
            <p>{t('reveal.unavailable.body')}</p>
          </DegradedState>
        </div>
      ) : null}

      {/* THE ATTEMPT IS STILL OPEN — a lost connection, an untyped 5xx, or a server that says this
          key is still executing. Its own state, above the refusal surface, because it is not a
          refusal: the grant is kept and the act beside it is a REPLAY under the same key.

          IT ALSO HOLDS THE ONLY WAY OUT THAT IS NOT THE REPLAY (round-5 A4). While it stands the
          primary control below is disabled, so this panel owes the operator an exit — one that
          states its price before they take it, rather than a button that silently mints a new key
          over an attempt that may have committed a disclosure.

          AND IT IS THIS SUBJECT'S (P13-m ITEM 1): it is drawn for whichever row holds an open
          question, whether or not that row is the one the last request was sent for.

          IT SAYS WHICH KIND OF OPEN IT IS (P13-n). The rules are one set; the words are two. "The
          outcome is not known here" is true of a lost answer and false of `idempotency.in_progress`,
          where the server has just said what is happening — so that one gets its own title, its own
          explanation of why the panel is held, and its own statement of what abandoning would drop.
          The COLLECT is not repeated here: `RefusalWithRemedy` below draws it on the decided-conflict
          surface for this exact code, pointed at the same `collect`. */}
      {pending ? (
        <div data-testid={pending.kind === 'in_progress' ? 'reveal-outcome-in-progress' : 'reveal-outcome-unknown'}>
          <DegradedState title={pending.kind === 'in_progress' ? t('reveal.running') : t('reveal.unknown')}>
            <p>{pending.kind === 'in_progress' ? t('reveal.running.body') : t('reveal.unknown.body')}</p>
            <p>{pending.kind === 'in_progress' ? t('reveal.running.blocked') : t('reveal.unknown.blocked')}</p>
            <p>
              <strong>
                {pending.kind === 'in_progress'
                  ? t('reveal.running.abandon.warning')
                  : t('reveal.unknown.abandon.warning')}
              </strong>
            </p>
            <button
              type="button"
              className="btn btn--secondary btn--small"
              data-testid="reveal-abandon"
              onClick={abandonAttempt}
            >
              {t('reveal.unknown.abandon')}
            </button>
          </DegradedState>
        </div>
      ) : null}

      {/* `retryExact` RE-SENDS THE INTENT, not a fresh one: same key, same body, same handling. A
          retry that minted a new key would turn the ambiguous case into a second reveal. `collect`
          re-sends THIS SUBJECT's held intent where there is one, because the hook's own `lastIntent`
          may belong to a reveal the operator made for somebody else in between. */}
      {shownError ? <RefusalWithRemedy error={shownError} action={REVEAL} retryExact={collect} /> : null}

      {/* THE ORDINARY CONTROL STANDS DOWN WHILE AN ATTEMPT IS OPEN (round-5 A4; P13-n).
          It used to stay live under the unknown panel, and pressing it minted a new key over the
          ambiguous intent — the operator most likely to press it being the one looking at "the
          outcome is not known here". The panel above carries the two acts that are legitimate in
          that state: collect the answer, or abandon the attempt on purpose. */}
      <button className="btn" type="button" disabled={busy || !ready || pending !== null} onClick={run}>
        {t('reveal.run')}
      </button>

      {revealed ? (
        <>
          {disclosedFields(revealed).length > 0 ? (
            <SuccessNote>{t('reveal.audited', { customer: customerId ?? '' })}</SuccessNote>
          ) : (
            // 200, and nothing was disclosed. Saying "the reveal was recorded" here would invent an
            // audit row for a request that took the branch which writes none.
            <div data-testid="reveal-nothing">
              <DegradedState title={t('reveal.nothing')}>
                <p>{t('reveal.nothing.body')}</p>
              </DegradedState>
            </div>
          )}
          <dl className="kv" data-testid="revealed-fields">
            {/* ONLY what the server returned. A field it withheld is simply absent. */}
            {Object.entries(revealed).map(([k, v]) =>
              v === undefined || v === null || v === '' ? null : (
                <div key={k}>
                  <dt>{k}</dt>
                  <dd className="ltr">{String(v)}</dd>
                </div>
              ),
            )}
          </dl>
        </>
      ) : null}

      <DegradedState title={t('reveal.trail')}>
        <p className="muted">{t('reveal.trail.local')}</p>
        {log.length === 0 ? (
          <p className="muted">{t('reveal.none')}</p>
        ) : (
          <ul data-testid="reveal-local-log">
            {log.map((entry) => (
              <li key={entry.seq} className="ltr">
                {formatDateTime(entry.at)} · {entry.customerId.slice(0, 8)} · {entry.outcome} ·{' '}
                {/* The server-side half is claimed ONLY for the attempts that actually disclosed —
                    and an attempt whose answer was lost, or whose answer said it is still running,
                    claims nothing in either direction. */}
                {entry.audit === 'written'
                  ? t('reveal.audited.badge')
                  : entry.audit === 'nothing_revealed'
                    ? t('reveal.audited.nothing')
                    : entry.audit === 'unavailable'
                      ? t('reveal.audited.unavailable')
                      : entry.audit === 'unknown'
                        ? t('reveal.audited.unknown')
                        : entry.audit === 'in_progress'
                          ? t('reveal.audited.running')
                          : t('reveal.audited.none')}{' '}
                · {entry.purpose} · {entry.caseRef}
              </li>
            ))}
          </ul>
        )}
      </DegradedState>
    </Card>
  );
}

function CustomerTimeline({ customerId }: { customerId: string }): ReactElement {
  const { t, formatDateTime } = useI18n();
  const timeline = useApiList<TimelineEntry>(`/v1/customers/${customerId}/attribution-timeline`);
  const assign = useApiAction(['/v1/customers']);
  const [orgId, setOrgId] = useState('');
  const [expoId, setExpoId] = useState('');

  return (
    <>
      <Card title={t('brk.timeline.title')} description={t('brk.timeline.desc')}>
        {timeline.isLoading ? <LoadingState /> : null}
        {timeline.error ? <ErrorState error={timeline.error} onRetry={() => void timeline.refetch()} /> : null}
        {timeline.data ? (
          <DataTable<TimelineEntry>
            caption={t('brk.timeline.title')}
            rows={timeline.data.items ?? []}
            rowKey={(e, ) => `${e.at}|${e.kind}`}
            empty={<EmptyState />}
            columns={[
              { key: 'at', header: t('table.created'), cell: (e) => formatDateTime(e.at) },
              { key: 'kind', header: t('table.kind'), cell: (e) => <Ltr>{e.kind}</Ltr> },
              {
                key: 'detail',
                header: 'detail',
                cell: (e) => <Ltr>{JSON.stringify(e.detail)}</Ltr>,
              },
            ]}
          />
        ) : null}
      </Card>

      <Card title={t('admin.leads.title')}>
        <AuditStatement>
          <p>
            <Ltr>lead.assign_brokerage</Ltr> — routing is SERVICING ONLY. The acquisition record
            stays <Ltr>direct</Ltr> and is never rewritten; commercial credit on a resulting deal is
            the platform’s, with the servicing brokerage recorded. An open attribution exception
            freezes nothing and does not block this.
          </p>
        </AuditStatement>
        <Field label="to_brokerage_org_id" name="assign-org" value={orgId} onChange={setOrgId} ltr required />
        <Field label="expo_id" name="assign-expo" value={expoId} onChange={setExpoId} ltr required />
        {assign.error ? <ErrorState error={assign.error} /> : null}
        <button
          className="btn"
          type="button"
          disabled={assign.isPending || orgId === '' || expoId === ''}
          onClick={() =>
            assign.mutate(newIntent({
              path: `/v1/customers/${customerId}:assign-brokerage`,
              body: { to_brokerage_org_id: orgId, expo_id: expoId },
            }))
          }
        >
          {t('action.submit')}
        </button>
      </Card>
    </>
  );
}

/** `ADM-38` — the platform's own unrouted population, as a queue rather than a filter on a list. */
export function AdminDirectLeads(): ReactElement {
  const { t } = useI18n();
  const [expoId, setExpoId] = useState('');
  const leads = useApiList<LeadRow>('/v1/leads/direct', expoId ? { expo_id: expoId } : undefined);

  return (
    <>
      <PageTitle title={t('admin.leads.title')}>{t('admin.leads.desc')}</PageTitle>
      <Card title={t('admin.leads.title')}>
        <Field label="expo_id" name="direct-expo" value={expoId} onChange={setExpoId} ltr />
        {leads.isLoading ? <LoadingState /> : null}
        {leads.error ? <ErrorState error={leads.error} onRetry={() => void leads.refetch()} /> : null}
        {leads.data ? (
          <DataTable<LeadRow>
            caption={t('admin.leads.title')}
            rows={leads.data.items ?? []}
            rowKey={(l) => `${l.customer_id}|${l.expo_id}`}
            empty={<EmptyState title="no unrouted direct customers for this expo">{t('admin.leads.desc')}</EmptyState>}
            columns={[
              { key: 'customer', header: t('table.identifier'), cell: (l) => <Ltr>{l.customer_id.slice(0, 8)}</Ltr> },
              { key: 'phone', header: 'phone', cell: (l) => <Ltr>{l.phone_masked}</Ltr> },
              { key: 'source', header: 'source_type', cell: (l) => <Ltr>{l.source_type}</Ltr> },
              {
                key: 'broker',
                header: 'broker_user_id',
                cell: (l) => (l.broker_user_id ? <Ltr>{l.broker_user_id.slice(0, 8)}</Ltr> : t('table.none')),
              },
            ]}
          />
        ) : null}
      </Card>
    </>
  );
}

/**
 * `ADM-18` — exceptional resolution. Super Admin only, step-up AND a real second principal's
 * recorded approval, and no countdown.
 *
 * WHAT CHANGED, AND WHY THIS SCREEN LOOKS LIKE ITS NEIGHBOURS NOW (BE-3).
 *
 * It used to carry an `approver_user_id` text box. That was an honest rendering of a dishonest
 * route: `crm.ResolveException` read the id out of the RESOLVER's own body and checked only that the
 * named principal WOULD hold `attribution.exception.resolve` — eligibility, not a decision. The
 * named actor took no action, was never authenticated for this act, was never inside a step-up
 * window of its own, and need never have learned of it, so one Platform Super Admin could move a
 * customer's servicing ownership and a deal's commercial credit alone and leave a record naming a
 * colleague. There was no approval row to hand over, so this screen refused to dress the box up as a
 * ceremony and said so instead.
 *
 * The backend closed it. `POST /v1/approvals` gained the `attribution_exception` aggregate — the
 * EXCEPTION at its own `version`, the number the resolution's terminal CAS already fences on, and
 * expo-less because the resolve key is Platform-Super-Admin-scoped — with a proposal of its own, and
 * `crm.ResolveException` consumes the row inside its transaction with the approver's authority
 * re-checked under lock. So the ceremony is real, and this screen performs it through exactly the
 * machinery every other four-eyes act uses.
 *
 * THE FIELD IS GONE RATHER THAN HIDDEN: `decodeStrict` answers 400 for `approver_user_id`, so a
 * screen that still sent it would fail every resolution.
 *
 * THE CORRECTION FIELDS ARE HERE BECAUSE THE DIGEST BINDS THEM. `crm.ExceptionResolutionDigest`
 * covers (exception, decision, new brokerage, new broker, expo, sorted deal ids); an approval bound
 * to a resolution the screen could not state would be an approval for a coordinate.
 */
export function AdminExceptions(): ReactElement {
  const { t } = useI18n();
  const session = useSession();
  const exceptions = useApiList<AttributionException>('/v1/attribution-exceptions');
  const resolve = useApiAction<AttributionException>(['/v1/attribution-exceptions']);
  const [target, setTarget] = useState<AttributionException | null>(null);
  const [decision, setDecision] = useState('corrected');
  const [summary, setSummary] = useState('');
  const [newBrokerage, setNewBrokerage] = useState('');
  const [newBroker, setNewBroker] = useState('');
  const [correctionExpo, setCorrectionExpo] = useState('');
  const [dealIds, setDealIds] = useState('');
  const [grant, setGrant] = useState<HandoffGrant | null>(null);

  const stepUp = session.grantFor(EXCEPTION_RESOLVE);

  /**
   * The deal ids, as the operator typed them: comma or whitespace separated, blanks dropped.
   *
   * They are NOT sorted here. `intentDigest` and `crm.ExceptionResolutionDigest` both sort, so the
   * binding is order-insensitive either way, and re-ordering the operator's own list under them
   * would only make the field disagree with what they typed.
   */
  const deals = dealIds
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter((s) => s !== '');

  /**
   * THE ACT, STATED IN FULL, or nothing at all.
   *
   * A `corrected` outcome is only fully specified once the corrected owner is named — that is what
   * the approver is being asked to decide — so until then the hand-off says the request is
   * incomplete rather than minting a token bound to a half-stated correction. A `rejected` outcome
   * moves no ownership, so it is complete as soon as an exception is selected.
   */
  const uuidish = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const idsOk = [newBrokerage, newBroker, correctionExpo]
    .filter((v) => v !== '')
    .every((v) => uuidish.test(v)) && deals.every((d) => uuidish.test(d));

  const handoff: HandoffRequest | null =
    target && idsOk
      ? {
          action: EXCEPTION_RESOLVE,
          aggregateType: 'attribution_exception',
          aggregateId: target.id,
          expectedVersion: target.version,
          reasonCode: 'dual_control',
          exception: {
            decision,
            ...(newBrokerage ? { new_brokerage_org_id: newBrokerage } : {}),
            ...(newBroker ? { new_broker_user_id: newBroker } : {}),
            ...(correctionExpo ? { expo_id: correctionExpo } : {}),
            ...(deals.length > 0 ? { deal_ids: deals } : {}),
          },
          label: `resolve ${target.id} as ${decision}`,
        }
      : null;

  const run = (): void => {
    if (!target || !grant) return;
    resolve.mutate(newIntent({
      path: `/v1/attribution-exceptions/${target.id}:resolve`,
      action: EXCEPTION_RESOLVE,
      label: `resolve ${target.id}`,
      // THE APPROVAL IS NAMED, THE APPROVER IS NOT. `crm.ResolveException` matches on the BINDING
      // and takes the approver out of the row it consumed; `decodeStrict` refuses
      // `approver_user_id` outright, which is what makes the credential un-forwardable.
      body: {
        decision,
        approval_id: grant.approvalId,
        summary,
        ...(newBrokerage ? { new_brokerage_org_id: newBrokerage } : {}),
        ...(newBroker ? { new_broker_user_id: newBroker } : {}),
        ...(correctionExpo ? { expo_id: correctionExpo } : {}),
        ...(deals.length > 0 ? { deal_ids: deals } : {}),
      },
      ifMatch: target.version,
    }));
  };

  return (
    <>
      <PageTitle title={t('admin.exceptions.title')}>{t('admin.exceptions.desc')}</PageTitle>
      <Card title={t('admin.exceptions.title')}>
        {exceptions.isLoading ? <LoadingState /> : null}
        {exceptions.error ? <ErrorState error={exceptions.error} onRetry={() => void exceptions.refetch()} /> : null}
        {exceptions.data ? (
          <DataTable<AttributionException>
            caption={t('admin.exceptions.title')}
            rows={exceptions.data.items ?? []}
            rowKey={(e) => e.id}
            empty={<EmptyState>{t('admin.exceptions.desc')}</EmptyState>}
            columns={[
              {
                key: 'id',
                header: t('table.identifier'),
                cell: (e) => (
                  <button type="button" className="btn btn--secondary btn--small" onClick={() => setTarget(e)}>
                    <Ltr>{e.id.slice(0, 8)}</Ltr>
                  </button>
                ),
              },
              { key: 'category', header: 'category', cell: (e) => <Ltr>{e.category}</Ltr> },
              {
                key: 'status',
                header: t('table.status'),
                cell: (e) => <Badge tone={statusTone(e.status)}>{e.status}</Badge>,
              },
              { key: 'customer', header: 'customer_id', cell: (e) => <Ltr>{e.customer_id.slice(0, 8)}</Ltr> },
              { key: 'summary', header: 'summary', cell: (e) => e.summary ?? t('table.none') },
            ]}
          />
        ) : null}
      </Card>

      {target ? (
        <Card title={`resolve · ${target.id.slice(0, 8)}`}>
          <AuditStatement>
            <p>
              {/* BE-3. This used to say the act performed a "named attestation" and NOT four eyes,
                  which was true of the route as it then stood. `crm.ResolveException` now consumes
                  a real `iam_approvals` row inside its own transaction, so the statement describes
                  the record the server actually writes. */}
              <Ltr>attribution.exception.resolve</Ltr> — Super Admin only. It needs your step-up AND a
              second Platform Super Admin&rsquo;s <strong>recorded</strong> approval: they decide in
              their own session, under their own step-up, bound to this exception at the version
              below and to the resolution you state. The server consumes that approval inside the
              resolution&rsquo;s transaction — so a resolution that fails afterwards does not burn
              their decision, and the same approval cannot decide twice — and writes the approver it
              consumed onto the record. A correction writes before/after snapshots and creates
              ownership rows with reason <Ltr>exception_correction</Ltr>.
            </p>
          </AuditStatement>
          <Select
            label="decision"
            name="decision"
            value={decision}
            options={['corrected', 'rejected'].map((d) => ({ value: d, label: d }))}
            onChange={setDecision}
          />
          <TextArea label="summary" name="ex-summary" value={summary} onChange={setSummary} required />
          {/* THE CORRECTED OWNER, WHICH IS WHAT THE APPROVAL BINDS. Empty means the correction moves
              no ownership row; `crm.ExceptionResolutionDigest` renders an unnamed id as `-`, so
              "none" is a value in the binding rather than an absence from it. */}
          {decision === 'corrected' ? (
            <>
              <Field
                label="new_brokerage_org_id"
                name="ex-brokerage"
                value={newBrokerage}
                onChange={setNewBrokerage}
                ltr
                {...(newBrokerage && !uuidish.test(newBrokerage) ? { error: 'not a uuid' } : {})}
              />
              <Field
                label="new_broker_user_id"
                name="ex-broker"
                value={newBroker}
                onChange={setNewBroker}
                ltr
                {...(newBroker && !uuidish.test(newBroker) ? { error: 'not a uuid' } : {})}
              />
              <Field
                label="expo_id"
                name="ex-expo"
                value={correctionExpo}
                onChange={setCorrectionExpo}
                ltr
                {...(correctionExpo && !uuidish.test(correctionExpo) ? { error: 'not a uuid' } : {})}
              />
              <Field
                label="deal_ids"
                name="ex-deals"
                value={dealIds}
                onChange={setDealIds}
                ltr
                hint={t('exception.deals.hint')}
                {...(deals.some((d) => !uuidish.test(d)) ? { error: 'not a uuid' } : {})}
              />
            </>
          ) : null}
          {stepUp ? null : <StepUpPanel action={EXCEPTION_RESOLVE} />}
          {/* THE SAME MACHINERY THE RELEASE, TAKEDOWN AND RECORDING-POLICY SCREENS USE. Nothing here
              belongs to the approver: the token carries FACTS, and the credential is the row. */}
          <div className="guard">
            <span className="guard__label">{t('exception.resolve.title')}</span>
            <p>{t('exception.resolve.recorded')}</p>
          </div>
          <ApprovalHandoff request={handoff} onGranted={setGrant} />
          {resolve.error ? (
            <RefusalWithRemedy
              error={resolve.error}
              action={EXCEPTION_RESOLVE}
              retryExact={resolve.retryLast}
              onReread={() => {
                // THE GRANT DIES WITH THE VERSION IT WAS BOUND TO. An approval is for one version of
                // one aggregate, so re-reading the exception must not leave a grant on screen that
                // the reread has already invalidated.
                setGrant(null);
                setTarget(null);
                void exceptions.refetch();
              }}
            />
          ) : null}
          <button
            className="btn"
            type="button"
            disabled={resolve.isPending || !grant || summary.trim() === ''}
            onClick={run}
          >
            {t('action.submit')}
          </button>
        </Card>
      ) : null}
    </>
  );
}

/** `ADM-16` — identity merge and split. */
export function AdminIdentities(): ReactElement {
  const { t } = useI18n();
  const session = useSession();
  const act = useApiAction(['/v1/identities']);
  const [survivor, setSurvivor] = useState('');
  const [absorbed, setAbsorbed] = useState('');
  const [reason, setReason] = useState(EXCEPTION_CATEGORIES[0] ?? 'administrative_error');
  const [rationale, setRationale] = useState('');
  const [grant, setGrant] = useState<HandoffGrant | null>(null);
  const [typedAbsorbed, setTypedAbsorbed] = useState('');

  const stepUp = session.grantFor(IDENTITY_MERGE);
  const uuidish = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  // A MERGE'S APPROVAL IS AN IDENTITY APPROVAL, not a `person` approval on `/v1/approvals`. The
  // merge spends `approval_id` from `POST /v1/identity-approvals` and can see no other row.
  const handoff: HandoffRequest | null =
    uuidish.test(survivor) && uuidish.test(absorbed)
      ? {
          kind: 'identity',
          action: IDENTITY_MERGE,
          survivorPersonId: survivor,
          absorbedPersonId: absorbed,
          reasonCode: reason,
          label: `merge ${absorbed} into ${survivor}`,
        }
      : null;

  const run = (): void => {
    if (!grant) return;
    act.mutate(
      newIntent({
        path: '/v1/identities:merge',
        action: IDENTITY_MERGE,
        label: `merge ${absorbed} into ${survivor}`,
        confirm: { expected: absorbed, typed: typedAbsorbed },
        body: {
          survivor_person_id: survivor,
          absorbed_person_id: absorbed,
          approval_id: grant.approvalId,
          // `oneOf("reason_code", …, "rationale", …)` accepts EXACTLY ONE of the two; sending both
          // was a validation refusal before the merge was ever attempted.
          rationale,
        },
      }),
    );
  };

  return (
    <>
      <PageTitle title={t('admin.identity.title')}>{t('admin.identity.desc')}</PageTitle>
      <Card title="merge">
        <AuditStatement>
          <p>
            <Ltr>identity.merge</Ltr> — step-up AND four eyes. Acquisition history is preserved as
            written; aliases and resolution history are created instead. Overlapping current
            ownership intervals block the merge until they are resolved explicitly.
          </p>
        </AuditStatement>
        <Field label="survivor_person_id" name="survivor" value={survivor} onChange={setSurvivor} ltr required />
        <Field label="absorbed_person_id" name="absorbed" value={absorbed} onChange={setAbsorbed} ltr required />
        <Select
          label={t('guard.reason')}
          name="identity-reason"
          value={reason}
          options={EXCEPTION_CATEGORIES.map((c) => ({ value: c, label: c }))}
          onChange={setReason}
        />
        <TextArea label="rationale" name="rationale" value={rationale} onChange={setRationale} required />
        <Field
          label={t('guard.typed.label', { value: absorbed || '—' })}
          name="merge-typed"
          value={typedAbsorbed}
          onChange={setTypedAbsorbed}
          ltr
          {...(typedAbsorbed && typedAbsorbed !== absorbed ? { error: t('guard.typed.mismatch') } : {})}
        />
        {stepUp ? null : <StepUpPanel action={IDENTITY_MERGE} />}
        <ApprovalHandoff request={handoff} onGranted={setGrant} />
        {act.error ? (
          <RefusalWithRemedy error={act.error} action={IDENTITY_MERGE} retryExact={act.retryLast} />
        ) : null}
        <button
          className="btn btn--destructive"
          type="button"
          disabled={
            act.isPending ||
            !grant ||
            rationale.trim() === '' ||
            typedAbsorbed !== absorbed ||
            absorbed === ''
          }
          onClick={run}
        >
          merge
        </button>
      </Card>
    </>
  );
}
